import { Select, SideNavigation, FormField, SelectProps } from '@cloudscape-design/components';
import React, { useContext, useMemo } from 'react';
import useOnFollow from '@@/hooks/use-on-follow';
import { SideNavigationProps, Spinner } from '@cloudscape-design/components';
import { qicPermissionMapping } from '@@/modules/portal/qic/common/permission-mappings';
import { mapModulesToSideNavigaton, ModuleAttribute } from '@@/lib/permissions';
import pbrPermissionMapping from '@@/modules/portal/proficiency-based-routing/common/permission-mappings';
import { AuthContext } from '@@/contexts/AuthContext';
import centenePermissionMapping from '@@/modules/portal/centene/common/permission-mappings';
import bulkEditPermissionMapping from '../modules/bulk-edit/common/permission-mapping';
import graclPermissionMapping from '../modules/gracl/common/permission-mappings';
import { insightsPermissionMapping } from '@@/modules/portal/insights/common/permission-mapping';
import dfcPermissionMapping from '@@/modules/portal/dfc/common/permission-mapping';
import { campaignsPermissionMapping } from '@@/modules/portal/campaigns/common/permission-mapping';

const mapStringToOptionDefinition = (label?: string | null, value?: string | null): SelectProps.Option | null => {
  if (!label || !value) return null;
  return { label, value };
};

const moduleAttributeMappings: ModuleAttribute<string>[] = [
  {
    moduleUrlPart: 'centene',
    moduleSectionName: 'Centene',
    modulePermissions: centenePermissionMapping
  },
  {
    moduleUrlPart: 'qic',
    moduleSectionName: 'Q in Connect',
    modulePermissions: qicPermissionMapping
  },
  {
    moduleUrlPart: 'pbr',
    moduleSectionName: 'PBR',
    modulePermissions: pbrPermissionMapping
  },
  {
    moduleUrlPart: 'bulk-edit',
    moduleSectionName: 'Bulk Edit',
    modulePermissions: bulkEditPermissionMapping
  },
  {
    moduleUrlPart: 'insights',
    moduleSectionName: 'Insights',
    modulePermissions: insightsPermissionMapping
  },
  {
    moduleUrlPart: 'campaigns',
    moduleSectionName: 'Campaigns',
    modulePermissions: campaignsPermissionMapping
  },
  {
    moduleUrlPart: 'dfc',
    moduleSectionName: 'DFC',
    modulePermissions: dfcPermissionMapping
  },
  {
    moduleUrlPart: 'gracl',
    moduleSectionName: 'Change Management',
    modulePermissions: graclPermissionMapping
  }
];

const getNavBarHeader = (
  instances: SelectProps.Options,
  currentInstance: SelectProps.Option | null,
  setCurrentInstance: (instance: string) => void
): SideNavigationProps.Item[] => [
  {
    type: 'link',
    //@ts-expect-error - SideNavigationProps.Item expects string for text, but FormField component is needed here
    text: (
      <FormField label='Instance'>
        <Select
          options={instances}
          onChange={({ detail }) => setCurrentInstance(detail.selectedOption.value ?? '')}
          selectedOption={currentInstance}
        />
      </FormField>
    )
  },
  {
    type: 'divider'
  }
];

const sideNavLoadingState: SideNavigationProps.Item[] = [
  {
    type: 'link',
    //@ts-expect-error - SideNavigationProps.Item expects string for text, but Spinner component is needed here
    text: <Spinner />,
    href: '#'
  }
];

const sideNavErrorState: SideNavigationProps.Item[] = [
  {
    type: 'link',
    text: 'Oops! Something went wrong',
    href: '#'
  }
];

export default function NavigationPanel() {
  const onFollow = useOnFollow();

  const auth = useContext(AuthContext);

  const navBarHeader = useMemo(() => {
    return getNavBarHeader(
      auth.getDropdownInstanceOptions(),
      mapStringToOptionDefinition(auth?.instanceData?.instanceAlias, auth?.currentInstance),
      auth.setCurrentInstance
    );
  }, [auth]);

  const modulesNavOptions = useMemo(() => {
    if (!auth?.currentInstance || !auth.instanceData) return [];
    return mapModulesToSideNavigaton(
      moduleAttributeMappings,
      auth.instanceData?.permissions,
      auth.instanceData?.instanceAlias
    );
  }, [auth]);
  const { getPathWithInstanceAlias } = useContext(AuthContext);

  return (
    <SideNavigation
      onFollow={onFollow}
      header={{ href: getPathWithInstanceAlias('/portal'), text: 'CX Portal' }}
      items={
        !auth || auth.loading
          ? sideNavLoadingState
          : auth.error
            ? sideNavErrorState
            : [...navBarHeader, ...modulesNavOptions]
      }
    />
  );
}
